import { MetricsService } from '@thoughtspot/metrics';
import {
    A3AnalysisTypeEnum,
    AnalysisDetails,
    ChangeAnalysisSummary,
    ColumnType,
} from '/@src/services/generated/graphql-types';

export enum TranslationKeys {
    // Common
    CANCEL = 'CANCEL',
    CLOSE = 'CLOSE',
    SHOW_MORE = 'Facet.showMore',

    // Header
    SPOTIQ_HEADER_DESCRIPTION = 'spotIQ.insights.header.description',
    SPOTIQ_SINGLE_INSIGHT_HEADER_DESCRIPTION = 'spotIQ.insights.header.singleInsightDescription',
    SPOTIQ_HEADER_SAVE_INSIGHTS = 'spotIQ.insights.header.saveInsights',
    SPOTIQ_HEADER_EXPIRYTIME = 'spotIQ.insights.header.expiryTime',
    SPOTIQ_HEADER_EXPIRES_IN = 'spotIQ.insights.header.expireIn',
    SPOTIQ_HEADER_ORIGINAL_QUERY = 'spotIQ.insights.header.originalQuery',

    // change summary
    POSITIVE_CHANGE_DESCRIPTION = 'spotIQ.positiveChangeDescription',
    NEGATIVE_CHANGE_DESCRIPTION = 'spotIQ.negativeChangeDescription',
    ZERO_CHANGE_DESCRIPTION = 'spotIQ.zeroChangeDescription',

    // More Actions
    SPOTIQ_MORE_ACTION_SHARE = 'SHARE',
    SPOTIQ_MORE_ACTION_DELETE = 'DELETE',
    SPOTIQ_MORE_ACTION_CUSTOMIZE_ANALYSIS = 'a3.customAnalysisTitle',
    SPOTIQ_MORE_ACTION_JOB_RUNS = 'spotIQ.insights.header.jobRuns',
    SPOTIQ_MORE_ACTION_SCHEDULE_ANALYSIS = 'spotIQ.insights.header.scheduleAnalysis',

    // save and delete
    DELETE_TOAST_MESSAGE = 'universalDelete.successToastMessage',
    DELETE_TOAST_TITLE_MESSAGE = 'universalDelete.successToastSelectedMessage',
    DELETE_NO_ACCESS_BANNER_MESSAGE = 'universalDelete.noPermissionBannerMessage',
    CONFIRM_DELETE = 'CONFIRM_DELETE',
    DELETE_DESCRIPTION = 'spotIQ.insights.deleteModal.description',
    SAVED_TOAST_MESSAGE = 'ANSWER_NAME_SAVED',
    BANNER_ERROR_MESSAGE = 'metadataListPage.caption.ERROR',
    MULTI_DELETE_MESSAGE = 'spotIqList.multidelete.message',

    // Job Schedule
    MODAL_TITLE = 'spotIqList.rowMenu.scheduleAnalysis',
    SCHEDULE = 'schedule',
    UPDATE_SCHEDULE = 'spotIqList.jobSchedule.scheduleBtnText',
    SUCCESS_MESSAGE = 'spotIQ.jobSchedule.success',
    UPDATE_SUCCESS_MESSAGE = 'spotIQ.jobSchedule.updateSuccess',
    FAILURE_MESSAGE = 'spotIQ.jobSchedule.failure',

    // individual analysis
    INSIGHT_ANALYSIS_TITLE = 'spotiqInsight.insightAnalysisDetail.title',
    INSIGHT_ANALYSIS_SUBTITLE = 'spotiqInsight.insightAnalysisDetail.subtitle',
    INSIGHT_ANALYSIS_MEAN = 'spotiqInsight.insightAnalysisDetail.mean',
    ANALYSIS_STDDEV = 'spotiqInsight.insightAnalysisDetail.stdDev',
    ANALYSIS_MULTIPLIER = 'spotiqInsight.insightAnalysisDetail.multiplier',
    ANALYSIS_MEDIAN = 'spotiqInsight.insightAnalysisDetail.median',
    ANALYSIS_MEDIANABSDEV = 'spotiqInsight.insightAnalysisDetail.medianAbsDev',
    ANALYSIS_ALPHA = 'spotiqInsight.insightAnalysisDetail.alpha',
    ANALYSIS_MINABSCHNGRATIO = 'spotiqInsight.insightAnalysisDetail.minAbsChngRatio',
    ANALYSIS_MINICHNGRATIO = 'spotiqInsight.insightAnalysisDetail.minChangeRatio',
    ANALYSIS_PVALTHRSHLD = 'spotiqInsight.insightAnalysisDetail.pValThrshld',
    ANALYSIS_PVAL = 'spotiqInsight.insightAnalysisDetail.pVal',
    ANALYSIS_RELDIFF = 'spotiqInsight.insightAnalysisDetail.relDiff',

    // Analysis details
    ANALYSIS_DETAILS_MODAL_TITLE = 'spotIq.analysisDetailsModal.title',
    NO_INSIGHT_DISCOVERED = 'spotIq.analysisDetailsModal.noInsightDiscovered',
    INSIGHT_SUMMARY_HEADING = 'spotIq.analysisDetailsModal.insightSummaryHeading',
    INSIGHT_SUMMARY = 'spotIq.analysisDetailsModal.insightSummary',
    ORIGINAL_QUERY = 'spotIq.analysisDetailsModal.originalQuery',

    // Custom Analysis
    CURRENT_DATA_ONLY = 'a3.currentDataOnly',
    EXCLUDE_NULL_FROM_THIS_ANALYSIS = 'preferences.a3.excludeNullFromThisAnalysis',
    EXCLUDE_ZERO_MEASURE_FROM_THIS_ANALYSIS = 'preferences.a3.excludeZeroMeasureFromThisAnalysis',
    AUTOTUNE_DATE_BOUNDARY_FROM_THIS_ANALYSIS = 'preferences.a3.autotuneDateBoundaryFromThisAnalysis',
    CUSTOM_ANALYSIS_MODAL_TITLE = 'SPOT_IQ',
    NO_COLUMNS_FOUND = 'a3.noColumnsFound',
    SOMETHING_WENT_WRONG = 'error.validation.default',

    // Negative Feedback
    FEEDBACK_QUESTION_COLUMN_UNINTERESTING = 'a3.FEEDBACK_QUESTION_COLUMN_UNINTERESTING',
    FEEDBACK_QUESTION_FILTER_COLUMN_UNINTERESTING = 'a3.FEEDBACK_QUESTION_FILTER_COLUMN_UNINTERESTING',
    FEEDBACK_QUESTION_FILTER_COLUMN_UNINTERESTING_TOOLTIP = 'a3.FEEDBACK_QUESTION_FILTER_COLUMN_UNINTERESTING_TOOLTIP',
    FEEDBACK_COLUMN_AND_FILTER_VALUE = 'a3.FEEDBACK_COLUMN_AND_FILTER_VALUE',
    FEEDBACK_QUESTION_COLUMN_AND_FILTER_UNINTERESTING_TOOLTIP = 'a3.FEEDBACK_QUESTION_COLUMN_AND_FILTER_UNINTERESTING_TOOLTIP',
    FEEDBACK_QUESTION_TREND_BETTER_DATE_ATTRIBUTE = 'a3.FEEDBACK_QUESTION_TREND_BETTER_DATE_ATTRIBUTE',
    FEEDBACK_QUESTION_CORRELATION_OBVIOUS = 'a3.FEEDBACK_QUESTION_CORRELATION_OBVIOUS',
    FEEDBACK_QUESTION_ALL_ANOMALIES_OBVIOUS = 'a3.FEEDBACK_QUESTION_ALL_ANOMALIES_OBVIOUS',
    FEEDBACK_QUESTION_TREND_EXPECTED = 'a3.FEEDBACK_QUESTION_TREND_EXPECTED',
    FEEDBACK_QUESTION_ANOMALOUS_POINT_UNINTERESTING = 'a3.FEEDBACK_QUESTION_ANOMALOUS_POINT_UNINTERESTING',
    FEEDBACK_QUESTION_DIFF_VALUES_UNINTERESTING = 'a3.FEEDBACK_QUESTION_DIFF_VALUES_UNINTERESTING',
    FEEDBACK_QUESTION_DIFFS_EXPECTED = 'a3.FEEDBACK_QUESTION_DIFFS_EXPECTED',
    FEEDBACK_QUESTION_ALL_CHANGES_EXPECTED = 'a3.FEEDBACK_QUESTION_ALL_CHANGES_EXPECTED',
    FEEDBACK_QUESTION_DATA_SOURCE_UNINTERESTING = 'a3.FEEDBACK_QUESTION_DATA_SOURCE_UNINTERESTING',
    YES = 'YES',
    NO = 'NO',
    FEEDBACK_MODAL_TITLE = 'SpotIQ.negativeFeedback.modalTitle',
    FEEDBACK_TITLE = 'a3.feedback.dialog.title',
    GIVE_FEEDBACK = 'feedback.save',
    SKIP = 'SKIP',
    FEEDBACK_SUBTITLE = 'SpotIQ.negativeFeedback.subTitle',
    ANALYSIS_EXPIRED_MSG = 'spotIQ.analysisExpired.message',
    ANALYSIS_EXPIRED_BTN_TXT = 'spotIQ.analysisExpired.btnText',
    ANALYSIS_RESULT_GENERIC_ERROR_TITLE = 'spotIQ.analysisResult.genericErrorTitle',
    JOB_PAUSED_SUCCESS = 'spotIqList.pauseJob.success',
    JOB_RESUMED_SUCCESS = 'spotIqList.resumeJob.success',

    // Job runs
    JOB_RUNS_MODAL_TITLE = 'spotIQ.insights.header.jobRuns',
    JOB_RUNS_MODAL_SUBHEADER = 'jobRuns.modal.subHeader',
    JOB_RUNS_HINT = 'jobRuns.hint',
    JOB_RUNS_COMPLETEDIN = 'jobRuns.completedIn',
    NO_JOB_RUNS = 'jobRun.noJobRuns',

    // New job scheduled confirmation
    SCHEDULED_MODAL_TITLE = 'spotIQ.scheduledModal.title',
    SCHEDULED_MODAL_BODY = 'spotIQ.scheduledModal.body',
    OK = 'OK',
}
export interface SaveAnalysisResultProps {
    expiresIn: AnalysisResultExpiry;
    handleSave: () => void;
    isSaving: boolean;
}

export enum HeaderTestIds {
    saveInsight = 'save-insight',
    moreActions = 'spotIQ-more-actions',
}

export type AnalysisResultExpiry = {
    remainingDays: number;
    remainingHours: number;
    remainingMins: number;
};

interface PhrasedToken {
    token: string;
    tokenType: string;
}
export interface AnalysisResultHeaderProps {
    /**
     * analysis Result ID
     */
    analysisResultId: string;

    /*
     * spotIQ insight's title
     */
    title: string;

    /*
     * number of insights processed
     */
    numOfInsights: number;

    /*
     * number of rows processed
     */
    numOfRows: number;

    /*
     * time taken for processing
     */
    duration: number;

    /*
     * spotIQ insight's time remaining to expire
     */
    expiresIn: AnalysisResultExpiry;

    /*
     * spotIQ insight's original list of Query
     */
    queryList: PhrasedToken[];

    /**
     * call it to delete analysis result
     */
    deleteAnalysisResult: () => Promise<any>;

    /**
     * wheather to show loading
     */
    loading: boolean;

    /**
     * if true, show the delete confirmation modal
     */
    showDeleteConfirmation: boolean;

    /**
     * call it to show/hide the delete confirmation modal
     */
    setShowDeleteConfirmation: React.Dispatch<React.SetStateAction<boolean>>;

    /**
     * call it to show an error alert banner on top
     */
    showErrorBanner: (message: string) => void;

    /**
     *  whether to show the Save Insight button
     */
    isSaveVisible: boolean;

    /**
     * save button callback
     */
    handleSave: () => void;

    /**
     * if save is in progress
     */
    isSaving: boolean;

    /**
     * call to show success toast alert
     */
    showToastMessage: React.Dispatch<React.SetStateAction<string>>;

    /**
     *  contains analysis details used in the analysis details modal
     */
    analysisDetails: AnalysisDetails;
    /**
     *  metric service
     */
    metricsService?: MetricsService;
    /**
     * analysis type
     */
    analysisType?: A3AnalysisTypeEnum;

    /**
     *
     */
    changeSummary?: ChangeAnalysisSummary;

    /**
     * Flag to hide show the analysis report description
     */
    showDescription?: boolean;

    /**
     * menu actions for analysis
     */
    menuActions?: AnalysisActionIds[];
}

export const POLLING_INTERVAL = 60000;

export const LIST_POLL_INTERVAL = 4000;

export const PROGRESS_UPDATE_FREQUENCY = 250;

export const CORTEX_CACHE_POLL_INTERVAL = 10000;

export type SingleSelection = {
    name: string;
    label: string;
    id: string;
};

export enum PossibleOptionsInSingleSelection {
    YES = 'YES',
    NO = 'NO',
}

export type columnDataType = {
    column: string;
    columnId: string;
    table: string;
    isSelected: boolean;
    isSuggested: boolean;
    columnType: ColumnType;
};

export enum JobRunStatus {
    DEFAULT = 'DEFAULT',
    RESULT_EXPIRED = 'RESULT_EXPIRED',
    NO_INTERESTING_INSIGHTS = 'NO_INTERESTING_INSIGHTS',
    FAILED = 'FAILED',
    RUNNING = 'RUNNING',
    DONE = 'DONE',
}

export enum AnalysisActionIds {
    Share = 'Share',
    Delete = 'Delete',
    customize_analysis = 'customize_analysis',
    schedule_jobs = 'schedule_jobs',
    analysis_details = 'analysis_details',
}

export const forecastingAnalysisMenuActions = [
    AnalysisActionIds.Share,
    AnalysisActionIds.Delete,
];

export const defaultMenuActions = [
    AnalysisActionIds.Share,
    AnalysisActionIds.Delete,
    AnalysisActionIds.customize_analysis,
    AnalysisActionIds.schedule_jobs,
];
